import Axios from 'axios';
import { AuthUrls } from '@over-c/web-client-auth/lib/cjs/auth-config';

export type RuntimeConfig = {
  ssoUrl: string;
};

type TenantApiConfig = {
  apiBaseUrl: string;
  tenant?: string;
};

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  ssoUrl: 'https://sso-v7.over-c.net',
};

let runtimeConfig = DEFAULT_RUNTIME_CONFIG;

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function applyRuntimeConfig(): void {
  AuthUrls.authorization_endpoint = getSsoUrl('/oauth/authorize');
  AuthUrls.token_endpoint = getSsoUrl('/oauth/token');
  AuthUrls.revoke_token_endpoint = getSsoUrl('/oauth/token/revoke');
}

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  const config = value as RuntimeConfig;

  return (
    typeof value === 'object' &&
    value !== null &&
    typeof config.ssoUrl === 'string' &&
    config.ssoUrl.length > 0
  );
}

function normalizeOptionalUrl(url?: string): string | undefined {
  return url ? normalizeUrl(url) : undefined;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch('./config.json', { cache: 'no-store' });

    if (!response.ok) return runtimeConfig;

    const config = await response.json();

    if (isRuntimeConfig(config)) {
      runtimeConfig = {
        ssoUrl: normalizeUrl(config.ssoUrl),
      };
    }
  } catch {
    runtimeConfig = DEFAULT_RUNTIME_CONFIG;
  }

  applyRuntimeConfig();

  return runtimeConfig;
}

export function getSsoUrl(path: string): string {
  return `${runtimeConfig.ssoUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getTenantApiConfig(
  tenant?: string,
  apiBaseUrlOverride?: string
): TenantApiConfig | undefined {
  const apiBaseUrl = normalizeOptionalUrl(apiBaseUrlOverride);

  if (!apiBaseUrl) {
    return undefined;
  }

  return {
    apiBaseUrl,
    tenant: tenant || undefined,
  };
}

export function getTenantApiUrl(
  path: string,
  apiBaseUrlOverride?: string
): string {
  const tenantConfig = getTenantApiConfig(undefined, apiBaseUrlOverride);

  if (!tenantConfig) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  return `${tenantConfig.apiBaseUrl}${
    path.startsWith('/') ? path : `/${path}`
  }`;
}

export function getTenantApiHeaders(tenant?: string): Record<string, string> {
  return tenant ? { 'X-tenant': tenant } : {};
}

export async function goToLogout(): Promise<void> {
  try {
    await Axios.delete(getSsoUrl('/oauth/token/revoke'));
  } catch {
    // Continue to SSO logout even if token revocation fails.
  }

  window.location.assign(getSsoUrl('/logout'));
  await new Promise((resolve) => setTimeout(resolve, 3000));
}
